<template>
    <div>
        <form class="point-of-sale-search" @submit.prevent="searchByPointOfSale">
            <div class="field-wrapper">
                <label for="by-address">{{ t('find_label') }}</label>
                <AutocompleteInput
                    v-model="search"
                    id="by-address"
                    :placeholder="userPosition.length > 0 ? t('my_position') : ''"
                    :addresses="places"
                    :show-results-category-names="false"
                    :show-no-results-msg="false"
                    :hide-results="!showResult"
                    :relative="false"
                    @input="onSearchInput"
                >
                    <template #address="{ result }">
                        <button
                            type="button"
                            class="address-result-button"
                            @click="
                                searchByAddress(
                                    [result.address.coord.lat, result.address.coord.lon],
                                    `${result.address.house_number !== 0 ? result.address.house_number : ''} ${
                                        result.address.name
                                    }`,
                                )
                            "
                        >
                            <p class="results-item-name">
                                <template v-if="result.address.house_number !== 0">
                                    {{ result.address.house_number }}
                                </template>
                                {{ result.address.name }}
                            </p>
                            <small class="results-item-desc">
                                {{ result.address.administrative_regions[0].name }}
                            </small>
                        </button>
                    </template>
                    <template #button>
                        <div class="input-buttons">
                            <button v-if="allowClear" class="close-button" type="button" @click="resetSearch">
                                <span class="visuallyhidden">{{ $t('a11y.search.erase') }}</span>
                                <IconClose width="12" height="12" />
                            </button>
                            <button v-if="query.u !== 'u'" class="locate-button" type="button" @click="searchByUserPosition">
                                <span class="visuallyhidden">{{ $t('a11y.search.geolocation') }}</span>
                                <IconGeolocation width="24" height="24" />
                            </button>
                        </div>
                    </template>
                </AutocompleteInput>
            </div>
        </form>
        <AccordionList v-if="facets" tag="div" class="filters-wrapper">
            <AccordionItem tag="div" name="filters" class="filter-accordion">
                <template #title>
                    <div class="filters-title-wrapper">
                        <h2 class="filters-title">{{ t('filters_title') }}</h2>
                        <IconDropdownArrow class="chevron-icon" width="30" height="30" />
                    </div>
                </template>
                <template #content>
                    <form @submit.prevent="searchByPointOfSale">
                        <ul class="filter-list no-bullet">
                            <li v-for="[key, value] in Object.entries(facets)" :key="`facet-${key}`" class="checkbox-wrapper">
                                <template v-if="facets[key].length > 0 && value != 'container'">
                                    <input
                                        :id="`${key}-${value[0].values.value}`"
                                        v-model="filters[key]"
                                        type="checkbox"
                                        :value="`${value[0].values.value}`"
                                        @change="onFilterChange"
                                    />
                                    <label :for="`${key}-${value[0].values.value}`" class="filter-label">
                                        <img
                                            v-if="key === 'accessible'"
                                            src="@@/img/accessible.svg"
                                            width="18"
                                            height="18"
                                        />
                                        {{ t(key) }} ({{ value[0].values.count }})
                                    </label>
                                </template>
                            </li>
                        </ul>
                    </form>
                </template>
            </AccordionItem>
        </AccordionList>
        <DotsLoading v-if="isLoading" />
        <div v-else class="results-container">
            <TabList class="point-of-sale-tabs" :enabled="!isDesktop" :tabs="pointsOfSaleTabs">
                <template #tab0>
                    <div class="point-of-sale-list-wrapper">
                        <h3 class="list-title show-for-large">{{ t('list') }}</h3>
                        <PointOfSaleList
                            :points-of-sale="pointsOfSale"
                            :selected-point-index="selectedPointIndex"
                            :meta="meta"
                            @locatePoint="locatePoint"
                        />
                    </div>
                </template>
                <template #tab1>
                    <h3 class="map-title show-for-large">{{ t('map') }}</h3>
                    <PointOfSaleMap
                        class="map-container"
                        :points-of-sale="pointsOfSale"
                        :user-position="userPosition"
                        :point-position="pointPosition"
                        :address-position="addressPosition"
                        :selected-point-index="selectedPointIndex"
                        :meta="meta"
                    />
                </template>
            </TabList>
        </div>
        <LiveRegion ref="liveRegion" />
    </div>
</template>

<script setup>
import { useWindowSize, useDebounce } from '@vueuse/core';
import { onMounted, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import cms from '@@/js/api/cms';
import navitia from '@@/js/api/navitia';
import AccordionList from '@/shared/AccordionList';
import AccordionItem from '@/shared/AccordionItem';
import AutocompleteInput from '@/shared/AutocompleteInput';
import DotsLoading from '@/shared/DotsLoading';
import LiveRegion from '@/shared/LiveRegion.vue';
import PointOfSaleList from '@/points-of-sale/components/PointOfSaleList';
import PointOfSaleMap from '@/points-of-sale/components/PointOfSaleMap';
import TabList from '@/shared/TabList';

import IconClose from '@@/icons/close.svg';
import IconDropdownArrow from '@@/icons/dropdown-arrow.svg';
import IconGeolocation from '@@/icons/geolocation.svg';
import IconList from '@@/icons/list.svg';
import IconMapPin from '@@/icons/map-pin.svg';

defineProps({
    meta: {
        type: Object,
        default: null
    }
});

const { t } = useI18n({
    messages: {
        fr: {
            find_label: 'Entrer une adresse pour trouver les points de vente à proximité',
            list: 'Dans la liste',
            map: 'Sur la carte',
            my_position: 'Ma position',
            filters_title: 'Filtrer les résultats',
            lp_metropolitain: 'Vendeur métropolitain',
            centre_information: 'Émission de carte OPUS avec photo',
            accessible: 'Lieux accessibles aux personnes en fauteuil roulant seulement',
            carton_mrc: 'Carton pour le Transport Collectif de La Jacques-Cartier',
            centre_services: 'Centre de service',
            centre_information: 'Centre d’information',
            carton_plu_mobile: 'Carton pour PLUMobile',
            opus_card: 'Émission de carte OPUS avec photo',
            points_of_sale_list_updated: 'La liste des points de vente a été mise à jour.',
        },
        en: {
            find_label: 'Enter an address to find the nearest point of sale',
            list: 'In the list',
            map: 'On the map',
            find_button_label: 'Find',
            my_position: 'My position',
            filters_title: 'Filter results',
            lp_metropolitain: 'Metropolitan retailer',
            centre_information: 'OPUS card with photo available',
            accessible: 'Wheelchair accessible location only',
            carton_mrc: 'Card for Transport Collectif de La Jacques-Cartier',
            centre_services: 'Service centre',
            centre_information: 'Information centre',
            carton_plu_mobile: 'Card for PLUMobile',
            opus_card: 'OPUS card with photo available',
            points_of_sale_list_updated: 'Points of sale’s list has been updated.',
        },
    },
});

const currentUrl = ref(new URL(window.location));
const query = computed(() => Object.fromEntries(currentUrl.value.searchParams));

function updateUrl(newQuery) {
    const url = new URL(window.location);
    url.search = '';
    if (newQuery) {
        Object.entries(newQuery).forEach(([key, value]) => {
            if (value) {
                url.searchParams.set(key, value);
            }
        });
    }
    window.history.pushState({}, '', url);
    currentUrl.value = url;
}

const { width } = useWindowSize();
const debouncedWidth = useDebounce(width, 100);
const isDesktop = computed(() => debouncedWidth.value > 1024);

const pointsOfSaleTabs = [
    { name: t('list'), icon: IconList },
    { name: t('map'), icon: IconMapPin },
];

// Points de vente
const selectedPointIndex = ref(1);
const pointsOfSale = ref([]);
const count = ref(0);
const facets = ref({});
const filters = ref({});

// Position
const userPosition = ref([]);
const addressPosition = ref([]);
const pointPosition = ref([]);

// Recherche
const places = ref([]);
const showResult = ref(false);
const search = ref('');
const isLoading = ref(true);

const allowClear = computed(() => query.value.u);

async function updateAddresses(search) {
    if (search !== '' && search.length >= 3) {
        try {
            places.value = await navitia.getPlaces(search);
        } catch (error) {
            places.value = [];
        }
    } else {
        places.value = [];
    }
}

function searchByAddress(position, address) {
    search.value = address;
    showResult.value = false;
    updateUrl({
        l: position.toString(),
        u: 'a',
        a: address,
    });
}

function searchByUserPosition() {
    navigator.geolocation.getCurrentPosition(
        (position) => {
            userPosition.value = [position.coords.latitude, position.coords.longitude];
            updateUrl({
                f: Object.keys(filters.value).filter((k) => {
                    return filters.value[k];
                }),
                l: userPosition.value.toString(),
                u: 'u',
            });
        },
        (error) => {
            console.log(error);
        },
    );
}

function searchByPointOfSale() {
    if (places.value && places.value.length > 0) {
        const result = places.value[0];
        searchByAddress(
            [result.address.coord.lat, result.address.coord.lon],
            `${result.address.house_number !== 0 ? result.address.house_number : ''} ${result.address.name}`,
        );
    } else {
        resetSearch();
    }
}

function resetSearch() {
    userPosition.value = [];
    updateUrl();
}

function locatePoint(pointOfSale) {
    userPosition.value = [];
    addressPosition.value = [];
    pointPosition.value = [pointOfSale.latitude, pointOfSale.longitude];
    selectedPointIndex.value = pointOfSale.index;
}

function onFilterChange() {
    updateUrl({
        ...query.value,
        f: Object.keys(filters.value).filter((k) => filters.value[k]),
    });
}

const liveRegion = ref();

async function refreshSearchResults() {
    const queryParams = query.value;
    filters.value = {
        accessible: queryParams.f ? queryParams.f.includes('accessible') : false,
        opus_card: queryParams.f ? queryParams.f.includes('opus_card') : false,
        lp_metropolitain: queryParams.f ? queryParams.f.includes('lp_metropolitain') : false,
        carton_plu_mobile: queryParams.f ? queryParams.f.includes('carton_plu_mobile') : false,
        carton_mrc: queryParams.f ? queryParams.f.includes('carton_mrc') : false,
        centre_services: queryParams.f ? queryParams.f.includes('centre_services') : false,
        centre_information: queryParams.f ? queryParams.f.includes('centre_information') : false,
    };
    const response = await cms.getPointsOfSale(
        queryParams.u && queryParams.u === 'p' ? queryParams.s : '',
        queryParams.l || '46.811658279022,-71.21520582588',
        Object.keys(filters.value).filter((k) => filters.value[k]).map((k) => `${k}:1`),
    );
    count.value = response.count;
    facets.value = response.facets;

    pointsOfSale.value = response.results.filter((result) => result.id !== '');
    pointsOfSale.value.splice(1, 0, ...pointsOfSale.value.splice(pointsOfSale.value.findIndex((i) => parseInt(i.id) === 10), 1));
    pointsOfSale.value.forEach((point, index) => {
        point.index = index + 1;
    });
    pointPosition.value = pointsOfSale.value[0]
        ? [pointsOfSale.value[0].latitude, pointsOfSale.value[0].longitude]
        : [46.811658279022, -71.21520582588];

    search.value = queryParams.u === 'p' ? queryParams.s || '' : queryParams.u === 'a' ? queryParams.a || '' : '';
    userPosition.value = queryParams.u === 'u' ? queryParams.l.split(',') : [];
    addressPosition.value = queryParams.u === 'a' ? queryParams.l.split(',') : [];
    liveRegion.value.setMessage(t('points_of_sale_list_updated'));
}

function onSearchInput(event) {
    const value = event.target.value;
    updateAddresses(value);
    if (value === '') {
        showResult.value = false;
    } else {
        showResult.value = true;
    }
}

watch(
    () => query.value,
    () => {
        refreshSearchResults();
    },
    { deep: true }
);

onMounted(() => {
    refreshSearchResults();
    isLoading.value = false;
});
</script>

<style lang="scss" scoped>
.point-of-sale-search {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-bottom: 24px;
    background-color: $color-white;
    padding: 24px;
    border-radius: 20px;
    @include breakpoint(medium) {
        padding: 40px;
    }
    .field-wrapper {
        flex-grow: 1;
        position: relative;
        margin-bottom: 0;
        max-width: 700px;
    }
}
.address-result-button {
    @include accessible-focus;
    width: 100%;
    padding: 8px 16px;
    line-height: 1.5;
    text-align: left;
    background: transparent;
    border: 0;
    &:hover,
    &:focus-visible {
        background-color: $color-black-2;
    }
}
.results-item-name {
    @include rem-typo(18px);
    margin-bottom: 0;
}
.results-item-desc {
    @include rem-typo(12px);
    color: $color-black-50;
}
.input-buttons {
    z-index: 15;
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    align-items: center;
}
.close-button,
.locate-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: transparent;
    border: 0;
    border-radius: 50%;
    padding: 0;
}
.close-button {
    svg {
        fill: $color-black-60;
    }
}
.locate-button {
    svg {
        fill: $color-blue-60;
    }
}
.filters-wrapper {
    margin-bottom: 24px;
}
.filter-accordion {
    border-radius: 20px;
    overflow: hidden;
    background-color: $color-white;
    :deep(.accordion-button) {
        @include accessible-focus;
        background-color: $color-white;
    }
}
.filters-title-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    @include breakpoint(medium) {
        padding: 24px 40px;
    }
    .is-open & {
        border-bottom: 2px solid $color-black-2;
    }
}
.filters-title {
    @include rem-typo(16px);
    margin: 0;
    font-weight: 700;
}
.chevron-icon {
    padding: 7px;
    fill: $color-white;
    background-color: $color-blue-80;
    transition: transform $default-transition;
    border-radius: 50%;
    .is-open & {
        transform: rotate(180deg);
    }
}
.filter-list {
    padding: 16px 24px;
    @include breakpoint(medium) {
        padding: 32px 40px;
    }
}
.checkbox-wrapper {
    &:last-child {
        margin-bottom: 0;
    }
}
.filter-label {
    display: flex;
    align-items: center;
    gap: 4px;
}
.point-of-sale-tabs {
    position: relative;
    background-color: $color-white;
    padding: 16px;
    border-radius: 20px;
    @include breakpoint(large) {
        display: grid;
        grid-template-columns: 400px calc(100% - 400px - 32px);
        gap: 32px;
        padding: 0;
    }
}
.point-of-sale-list-wrapper {
    @include breakpoint(large) {
        padding: 40px 0 0 40px;
    }
}
.list-title,
.map-title {
    @include rem-typo(20px);
    margin: 0;
    padding-bottom: 16px;
}
.map-title {
    padding: 40px 0 16px;
}
.map-container {
    width: 100%;
    height: 450px;
    @include breakpoint(large) {
        height: 100vh;
        position: sticky;
        top: 0;
    }
}
</style>
